import {
  BufferAttribute,
  BufferGeometry,
  Points,
  ShaderMaterial,
  Color,
  MathUtils,
  Vector3,
  Group,
} from "three";
import { COLOR_GOLD_RGB, COLOR_METALLIC_BLUE_RGB } from "../../utils/constants";
import { NetworkParticles } from "./particles/NetworkParticles";
import { OrbParticles } from "./particles/OrbParticles";
import { BeakerParticles } from "./particles/BeakerParticles";
import { BlockchainParticles } from "./particles/BlockchainParticles";
import { EthParticles } from "./particles/EthParticles";
import { IParticleShape } from "./particles/VolumetricParticles.interface";
import { BlockchainArcs, BeakerArcs, OrbArcs, LatticeArcs } from "./arcs";

export enum ShapeType {
  NETWORK = 0,
  SPHERE = 1,
  BEAKER = 2,
  BLOCKCHAIN = 3,
  ETH = 4,
}

export class MorphParticles extends Points {
  private count = 15000;
  private size = 800;
  private geometryParticles: BufferGeometry;
  private materialParticles: ShaderMaterial;

  // Morph targets
  private positionsNetwork: Float32Array;
  private positionsSphere: Float32Array;
  private sphereTypes: Float32Array;
  private positionsBeaker: Float32Array;
  private beakerTypes: Float32Array;
  private positionsBlockchain: Float32Array;
  private blockchainTypes: Float32Array;
  private positionsEth: Float32Array;
  private ethTypes: Float32Array;

  // Transition state
  private currentFactors: number[] = [0, 0, 1, 0, 0];
  private targetFactors: number[] = [0, 0, 1, 0, 0];
  private transitionStartTime: number = -1;
  private transitionDuration: number = 3; // seconds
  private isTransitioning: boolean = false;

  // Shape-specific arcs
  private blockchainArcs: BlockchainArcs;
  private beakerArcs: BeakerArcs;
  private orbArcs: OrbArcs;
  private latticeArcs: LatticeArcs;

  constructor() {
    super();

    const network = new NetworkParticles();
    const orb = new OrbParticles();
    const beaker = new BeakerParticles();
    const blockchain = new BlockchainParticles();
    const eth = new EthParticles();

    const networkData = network.generate(this.count, this.size * 1.6);
    this.positionsNetwork = networkData.positions;

    const orbData = orb.generate(this.count, this.size * 1.3);
    this.positionsSphere = orbData.positions;
    this.sphereTypes = orbData.extraAttributes!["types"] as Float32Array;

    const beakerData = beaker.generate(this.count, this.size * 0.9);
    this.positionsBeaker = beakerData.positions;
    this.beakerTypes = beakerData.extraAttributes!["types"] as Float32Array;

    const blockchainData = blockchain.generate(this.count, this.size);
    this.positionsBlockchain = blockchainData.positions;
    this.blockchainTypes = blockchainData.extraAttributes![
      "types"
    ] as Float32Array;

    const ethData = eth.generate(this.count, this.size * 2);
    this.positionsEth = ethData.positions;
    this.ethTypes = ethData.extraAttributes!["types"] as Float32Array;

    this.geometryParticles = new BufferGeometry();
    // Initialize with Network
    this.geometryParticles.setAttribute(
      "position",
      new BufferAttribute(this.positionsNetwork, 3),
    );

    // Add other shapes as attributes
    this.geometryParticles.setAttribute(
      "posNetwork",
      new BufferAttribute(this.positionsNetwork, 3),
    );
    this.geometryParticles.setAttribute(
      "posSphere",
      new BufferAttribute(this.positionsSphere, 3),
    );
    this.geometryParticles.setAttribute(
      "aSphereType",
      new BufferAttribute(this.sphereTypes, 1),
    );
    this.geometryParticles.setAttribute(
      "posBeaker",
      new BufferAttribute(this.positionsBeaker, 3),
    );
    this.geometryParticles.setAttribute(
      "posBlockchain",
      new BufferAttribute(this.positionsBlockchain, 3),
    );
    this.geometryParticles.setAttribute(
      "posEth",
      new BufferAttribute(this.positionsEth, 3),
    );

    // Randoms for twinkling/noise
    const randoms = new Float32Array(this.count);
    for (let i = 0; i < this.count; i++) randoms[i] = Math.random();
    this.geometryParticles.setAttribute(
      "aRandom",
      new BufferAttribute(randoms, 1),
    );

    this.geometryParticles.setAttribute(
      "aBeakerType",
      new BufferAttribute(this.beakerTypes, 1),
    );

    this.geometryParticles.setAttribute(
      "aBlockchainType",
      new BufferAttribute(this.blockchainTypes, 1),
    );

    this.geometryParticles.setAttribute(
      "aEthType",
      new BufferAttribute(this.ethTypes, 1),
    );

    this.materialParticles = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMorphFactors: { value: [0, 0, 1, 0, 0] }, // Weights for Network, Sphere, Beaker, Blockchain, Eth
        uPixelRatio: { value: window.devicePixelRatio },
      },
      vertexShader: `
        uniform float uTime;
        uniform float uMorphFactors[5];
        uniform float uPixelRatio;
        
        attribute vec3 posNetwork;
        attribute vec3 posSphere;
        attribute vec3 posBeaker;
        attribute vec3 posBlockchain;
        attribute vec3 posEth;
        attribute float aRandom;
        attribute float aSphereType;
        attribute float aBeakerType;
        attribute float aBlockchainType;
        attribute float aEthType;
        
        varying float vAlpha;
        varying vec3 vColor;

        void main() {
          // Blockchain Animation Logic
          vec3 animatedBlockchain = posBlockchain;
          
          // Constants derived from size=800
          // blockSize = 400 (0.5 * 800), gap = 160 (0.2 * 800), stride = 560
          float period = 1680.0; // 3 * 560
          float halfPeriod = 840.0;
          
          // Move blocks to the right
          float speed = 150.0;
          float xOffset = uTime * speed;
          
          // Apply movement
          animatedBlockchain.x += xOffset;
          
          // Wrap around
          // We add halfPeriod before mod to handle negative numbers correctly in some GLSL versions, 
          // but mainly to center the wrap range around 0
          animatedBlockchain.x = mod(animatedBlockchain.x + halfPeriod, period) - halfPeriod;
          
          // Fade out at edges
          float distFromCenter = abs(animatedBlockchain.x);
          // Reduced fade range to keep blocks visible longer and gaps consistent
          float edgeFade = smoothstep(halfPeriod, halfPeriod - 50.0, distFromCenter);

          // Rotate 45 degrees around Z axis
          float angle = 0.785398; // 45 degrees
          float s = sin(angle);
          float c = cos(angle);
          float xNew = animatedBlockchain.x * c - animatedBlockchain.y * s;
          float yNew = animatedBlockchain.x * s + animatedBlockchain.y * c;
          animatedBlockchain.x = xNew;
          animatedBlockchain.y = yNew;

          // Eth Animation Logic (Liquid/Pulse for inner particles)
          vec3 animatedEth = posEth;
          // Check if we are rendering Eth and if particle is "inner" (Gold/Type 1.0)
          if (uMorphFactors[4] > 0.001 && aEthType > 0.9) {
            float t = uTime * 1.5;
            
            // 1. Pulse (Heartbeat) - Contract first then expand
            // Use -sin so as t increases from 0, we go negative (contract) first
            float pulse = 1.0 - 0.05 * sin(t * 2.0);
            animatedEth.x *= pulse;
            animatedEth.z *= pulse;
            
            // 2. Chaotic Swirl - Rotate slightly around Y axis
            // Vary rotation based on height to create liquid shear
            float angle = sin(t * 0.5 + posEth.y * 0.005) * 0.3; 
            float sa = sin(angle);
            float ca = cos(angle);
            float nx = animatedEth.x * ca - animatedEth.z * sa;
            float nz = animatedEth.x * sa + animatedEth.z * ca;
            animatedEth.x = nx;
            animatedEth.z = nz;
            
            // 3. Liquid Noise - Small random movements
            // Reduced amplitude to prevent bleeding
            animatedEth.x += sin(t + posEth.y * 0.05 + aRandom * 6.28) * 3.0;
            animatedEth.y += cos(t * 0.8 + posEth.x * 0.05 + aRandom * 6.28) * 3.0;
            animatedEth.z += sin(t * 1.2 + posEth.y * 0.05 + aRandom * 6.28) * 3.0;
          }

          vec3 pos = posNetwork * uMorphFactors[0] + 
                     posSphere * uMorphFactors[1] + 
                     posBeaker * uMorphFactors[2] + 
                     animatedBlockchain * uMorphFactors[3] +
                     animatedEth * uMorphFactors[4];

          // Add some noise/movement
          float noise = sin(uTime * 2.0 + aRandom * 10.0) * 5.0;
          pos.x += noise;
          pos.y += noise;
          pos.z += noise;

          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          
          // Smaller particles (reduced from 6.0 to 3.5)
          gl_PointSize = (6.5 * uPixelRatio) * (1000.0 / -mvPosition.z);
          
          // Twinkle
          float baseAlpha = 0.5 + 0.5 * sin(uTime * 3.0 + aRandom * 20.0);
          
          // Apply edge fade only when in Blockchain mode
          // We mix 1.0 (no fade) with edgeFade based on how much we are in Blockchain shape
          float finalFade = mix(1.0, edgeFade, uMorphFactors[3]);
          
          vAlpha = baseAlpha * finalFade;
          
          // Colors
          vec3 colorGold = vec3(${COLOR_GOLD_RGB.r}, ${COLOR_GOLD_RGB.g}, ${COLOR_GOLD_RGB.b});
          vec3 colorBlue = vec3(${COLOR_METALLIC_BLUE_RGB.r}, ${COLOR_METALLIC_BLUE_RGB.g}, ${COLOR_METALLIC_BLUE_RGB.b});
          
          // Default: Mostly Blue, some Gold
          vec3 defaultColor = mix(colorBlue, colorGold, step(0.9, aRandom));
          
          // Sphere: Blue Surface (0.0), Gold Inside (1.0)
          vec3 sphereColor = mix(colorBlue, colorGold, aSphereType);

          // Beaker: Blue Shell, Gold Liquid
          vec3 beakerColor = mix(colorGold, colorBlue, aBeakerType);

          // Blockchain: Blue Surface (0.0), Gold Inside (1.0)
          vec3 blockchainColor = mix(colorBlue, colorGold, aBlockchainType);
          
          // ETH: Blue Surface (0.0), Gold Inside (1.0)
          vec3 ethColor = mix(colorBlue, colorGold, aEthType);

          // Mix based on Sphere factor
          vec3 mixedColor = mix(defaultColor, sphereColor, uMorphFactors[1]);

          // Mix based on Beaker factor
          mixedColor = mix(mixedColor, beakerColor, uMorphFactors[2]);

          // Mix based on Blockchain factor
          mixedColor = mix(mixedColor, blockchainColor, uMorphFactors[3]);
          
          // Mix based on ETH factor
          vColor = mix(mixedColor, ethColor, uMorphFactors[4]);
        }
      `,
      fragmentShader: `
        varying float vAlpha;
        varying vec3 vColor;

        void main() {
          vec2 coord = gl_PointCoord - vec2(0.5);
          float dist = length(coord);
          
          if(dist > 0.5) discard;
          
          // Glow effect: brighter in center, fading out
          float strength = pow(1.0 - dist * 2.0, 1.5);
          
          gl_FragColor = vec4(vColor, vAlpha * strength);
        }
      `,
      transparent: true,
      depthWrite: false,
    });

    this.geometry = this.geometryParticles;
    this.material = this.materialParticles;
    this.frustumCulled = false;

    // Initialize blockchain arcs (added as child so they rotate with particles)
    this.blockchainArcs = new BlockchainArcs({
      blockSize: this.size * 0.5,
      blockGap: this.size * 0.2,
    });
    this.add(this.blockchainArcs);

    // Initialize beaker arcs (lightning in a bottle)
    this.beakerArcs = new BeakerArcs({
      size: this.size * 0.9, // Beaker uses size * 0.9
    });
    this.add(this.beakerArcs);

    // Initialize orb arcs (lightning from center to surface)
    this.orbArcs = new OrbArcs({
      size: this.size * 1.3, // Sphere uses size * 1.3
    });
    this.add(this.orbArcs);

    // Initialize lattice arcs (lightning along cube edges)
    this.latticeArcs = new LatticeArcs({
      size: this.size, // Network uses size * 1.6 (handled in LatticeArcs)
    });
    this.add(this.latticeArcs);
  }

  public update(time: number): void {
    if (this.materialParticles.uniforms["uTime"]) {
      this.materialParticles.uniforms["uTime"].value = time;
    }
    this.rotation.y = time * 0.1;

    if (this.isTransitioning) {
      if (this.transitionStartTime === -1) {
        this.transitionStartTime = time;
        if (this.materialParticles.uniforms["uMorphFactors"]) {
          this.currentFactors = [
            ...this.materialParticles.uniforms["uMorphFactors"].value,
          ];
        }
      }

      const t = Math.min(
        (time - this.transitionStartTime) / this.transitionDuration,
        1,
      );

      // Smoothstep for smoother transition
      const smoothT = t * t * (3 - 2 * t);

      const newFactors = this.currentFactors.map((start, i) => {
        const target = this.targetFactors[i] || 0;
        return start + (target - start) * smoothT;
      });

      this.setMorph(newFactors as [number, number, number, number, number]);

      if (t >= 1) {
        this.isTransitioning = false;
      }
    }

    // Update blockchain arcs - only show when fully morphed and not transitioning
    const blockchainFactor =
      this.materialParticles.uniforms["uMorphFactors"]?.value[
        ShapeType.BLOCKCHAIN
      ] ?? 0;
    const blockchainActive = !this.isTransitioning && blockchainFactor >= 1;
    this.blockchainArcs.setTransitionProgress(blockchainActive ? 1 : 0);
    this.blockchainArcs.updateBlockchain(time);

    // Update beaker arcs - only show when fully morphed and not transitioning
    const beakerFactor =
      this.materialParticles.uniforms["uMorphFactors"]?.value[
        ShapeType.BEAKER
      ] ?? 0;
    const beakerActive = !this.isTransitioning && beakerFactor >= 1;
    this.beakerArcs.setTransitionProgress(beakerActive ? 1 : 0);
    this.beakerArcs.updateBeaker(time);

    // Update orb arcs - only show when fully morphed and not transitioning
    const sphereFactor =
      this.materialParticles.uniforms["uMorphFactors"]?.value[
        ShapeType.SPHERE
      ] ?? 0;
    const orbActive = !this.isTransitioning && sphereFactor >= 1;
    this.orbArcs.setTransitionProgress(orbActive ? 1 : 0);
    this.orbArcs.updateOrb(time);

    // Update lattice arcs - only show when fully morphed and not transitioning
    const networkFactor =
      this.materialParticles.uniforms["uMorphFactors"]?.value[
        ShapeType.NETWORK
      ] ?? 0;
    const latticeActive = !this.isTransitioning && networkFactor >= 1;
    this.latticeArcs.setTransitionProgress(latticeActive ? 1 : 0);
    this.latticeArcs.updateLattice(time);
  }

  public transitionToShape(shape: ShapeType, duration: number = 3): void {
    this.transitionDuration = duration;
    this.targetFactors = [0, 0, 0, 0, 0];
    this.targetFactors[shape] = 1;
    this.isTransitioning = true;
    this.transitionStartTime = -1;
  }

  public setMorph(factors: [number, number, number, number, number]): void {
    if (this.materialParticles.uniforms["uMorphFactors"]) {
      this.materialParticles.uniforms["uMorphFactors"].value = factors;
    }
  }

  public setScroll(scrollPercent: number): void {
    // Map scroll 0..1 to morph factors
    // 0.0 - 0.25: Network -> Sphere
    // 0.25 - 0.5: Sphere -> Beaker
    // 0.5 - 0.75: Beaker -> Blockchain
    // 0.75 - 1.0: Blockchain -> Network

    let factors: [number, number, number, number, number] = [1, 0, 0, 0, 0];

    if (scrollPercent < 0.25) {
      const t = scrollPercent / 0.25;
      factors = [1 - t, t, 0, 0, 0];
    } else if (scrollPercent < 0.5) {
      const t = (scrollPercent - 0.25) / 0.25;
      factors = [0, 1 - t, t, 0, 0];
    } else if (scrollPercent < 0.75) {
      const t = (scrollPercent - 0.5) / 0.25;
      factors = [0, 0, 1 - t, t, 0];
    } else {
      const t = (scrollPercent - 0.75) / 0.25;
      factors = [t, 0, 0, 1 - t, 0];
    }

    this.setMorph(factors);
  }
}
